import { createAction, createSlice } from '@reduxjs/toolkit';
import {
  genFetch,
  IRequest,
  IRequestObject,
  RelationshipsEnums,
  reqHandler,
  RequestType,
  withToken,
} from 'coral-utility';
import { InstrumentEntity } from 'lib/types/entity';
import { put, PutEffect, SelectEffect, TakeEffect, takeLatest } from 'redux-saga/effects';
import { instruments } from 'state/api/instruments';
import { IInstrumentFile } from 'state/ducks/instrumentFiles/types';
import { addNotification } from 'state/ducks/notifications/actions';
import { NotificationElementOptions } from 'state/ducks/notifications/types';
import { convertBase64ToBlob } from 'state/utils/endpoints/request-helper';

export type CoreJsonResponse<R> = { data: R; message: string; meta: any };

export interface InstrumentSliceState {
  [x: string]: InstrumentEntity;
}

const initialState = {} as InstrumentSliceState;

/** Reducer Effects */
export const UPDATE_INSTRUMENT = 'instrument-slice/updateInstrument';

const updateInstrument = createAction<InstrumentEntity, typeof UPDATE_INSTRUMENT>(
  UPDATE_INSTRUMENT
);

/** Instrument Slice */
const instrumentSlice = createSlice({
  name: 'instrument-slice',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(updateInstrument, (state, action) => {
      const { id } = action.payload;
      state[id] = action.payload;
    });
  },
});

export default instrumentSlice.reducer;

interface InstrumentPayload {
  instrument: string;
}

interface InstrumentFormSubmission {
  id: string;
  body: object;
  element: NotificationElementOptions;
}

interface InstrumentFileSubmission {
  file: {
    file: Blob;
    fileName: string;
    category: string;
    originalName: string;
  };
  id: string;
}

export const GET_INSTRUMENT = 'instrument-slice/fetch/instrument';
export const EDIT_INSTRUMENT = 'instrument-slice/edit/instrument';
export const UPLOAD_INSTRUMENT = 'instrument-slice/upload/instrument/image';
export const DELETE_INSTRUMENT_IMAGE = 'instrument-slice/remove/instrument/image';

export const getInstrument = createAction<InstrumentPayload, typeof GET_INSTRUMENT>(GET_INSTRUMENT);
export const editInstrument = createAction<InstrumentFormSubmission, typeof EDIT_INSTRUMENT>(
  EDIT_INSTRUMENT
);

export const uploadInstrumentImage = createAction<
  InstrumentFileSubmission,
  typeof UPLOAD_INSTRUMENT
>(UPLOAD_INSTRUMENT);

export const removeInstrumentImage = createAction<
  { instrument: string; file: string },
  typeof DELETE_INSTRUMENT_IMAGE
>(DELETE_INSTRUMENT_IMAGE);

export type InstrumentSliceSagaActions = ReturnType<typeof updateInstrument>;

/**
 * Websocket Handlers
 *
 */

function* editSuccess(
  response: CoreJsonResponse<InstrumentEntity>,
  element?: NotificationElementOptions
) {
  try {
    const request: IRequestObject = {
      onFetch: () =>
        genFetch(instruments.getOne(response.data.id))(withToken())(
          RelationshipsEnums.INCLUDE_RELATIONS
        ),
      onSuccess: (response: CoreJsonResponse<InstrumentEntity>) =>
        put(updateInstrument(response.data)),
      onFailure: () => {},
      onDeserialize: 'json',
      type: RequestType.ONE,
    };

    yield reqHandler(request);
  } catch (error) {
    console.log('getInstrumentData Error:', error);
  }

  yield put(
    addNotification({
      message: 'Success',
      type: 'Success',
      element,
    })
  );
}

function* fetchInstrument(action: ReturnType<typeof getInstrument>) {
  try {
    const request: IRequestObject = {
      onFetch: () =>
        genFetch(instruments.getOne(action.payload.instrument))(withToken())(
          RelationshipsEnums.INCLUDE_ALL_RELATIONS
        ),
      onSuccess: (response: CoreJsonResponse<InstrumentEntity>) =>
        put(updateInstrument(response.data)),
      onFailure: () => {},
      onDeserialize: 'json',
      type: RequestType.ONE,
    };

    yield reqHandler(request);
  } catch (error) {
    console.log('getInstrumentData Error:', error);
  }
}

function* editInstrumentRequest(action: ReturnType<typeof editInstrument>) {
  try {
    const { id, body, element } = action.payload;
    const request: IRequestObject = {
      onFetch: () =>
        genFetch(instruments.edit(id, body))(withToken())(RelationshipsEnums.INCLUDE_ALL_RELATIONS),
      onSuccess: (response: CoreJsonResponse<InstrumentEntity>) => editSuccess(response, element),
      onFailure: (response: string) =>
        put(
          addNotification({
            message: 'failed',
            type: 'Failure',
            element,
          })
        ),
      onDeserialize: 'json',
      type: RequestType.ONE,
    };

    yield reqHandler(request);
  } catch (error) {
    console.log('getInstrumentData Error:', error);
  }
}

export function* addImage(action: ReturnType<typeof uploadInstrumentImage>) {
  try {
    const { file, id } = action.payload;
    const createFormData = (fileObj: any) => {
      const formData = new FormData();

      formData.append('file', fileObj.file, file.fileName);
      formData.append('category', fileObj.category);
      formData.append('originalName', fileObj.fileName);
      formData.append('name', fileObj.fileName);

      return formData;
    };

    const request: IRequestObject = {
      onFetch: () => genFetch(instruments.uploadFile(id, createFormData(file)))(withToken({}))(),
      onSuccess: (coreJsonResponse: any) => editSuccess({ data: { id: id } } as any, undefined),
      onFailure: (response: string) =>
        put(
          addNotification({
            message: 'failed',
            type: 'Failure',
          })
        ),
      onDeserialize: 'json',
      type: RequestType.ONE,
    };

    yield reqHandler(request);
  } catch (error) {
    console.log(error);
  }
}
function* removeImage(action: ReturnType<typeof removeInstrumentImage>) {
  try {
    const { file, instrument } = action.payload;
    const requests: IRequestObject = {
      onFetch: () => genFetch(instruments.deleteFile(file))(withToken())(),
      onSuccess: (coreJsonResponse: any) =>
        editSuccess({ data: { id: instrument } } as any, undefined),
      onFailure: () =>
        put(
          addNotification({
            message: 'failed',
            type: 'Failure',
          })
        ),
      onDeserialize: 'json',
      type: RequestType.ONE,
    };

    yield reqHandler(requests);
  } catch (error) {
    console.log(error);
  }
}

export function* instrumentSliceSaga() {
  yield takeLatest(GET_INSTRUMENT, fetchInstrument);
  yield takeLatest(EDIT_INSTRUMENT, editInstrumentRequest);
  yield takeLatest(UPLOAD_INSTRUMENT, addImage);
  yield takeLatest(DELETE_INSTRUMENT_IMAGE, removeImage);
}
